// @djsem/shared — Internal Event Bus (Prompt 24R).
// In-process, synchronous, deterministic pub/sub used by the Studio runtime. Reuses the P24
// observability trace as the log record (one observability system). Safety-priority rule:
// safe_mode/blackout events dispatch FIRST and cannot be suppressed by other subscribers.
//
// @see docs/state/agents/claude/prompts/102-repository-hardening-security-memory-oop-di-test-home.md

import { DJSEMObservabilityService, type DJSEMObservabilityTrace, type DJSEMTraceEventType } from "./observability";
import { SeqCounter } from "./runtime";

/** Dispatch-order priority for a bus event; `safety` always dispatches first. */
export type DJSEMBusEventPriority = "safety" | "high" | "normal" | "low";

/** A single event dispatched through the internal event bus. */
export interface DJSEMBusEvent {
  eventId: string;
  eventType: DJSEMTraceEventType | string;
  priority: DJSEMBusEventPriority;
  payload: unknown;
  createdAt: string;
}

/** A handle for a registered event-bus subscription; pass its `id` to {@link DJSEMInternalEventBus.unsubscribe}. */
export interface DJSEMBusSubscription {
  id: string;
  eventType: string | "*";
  handler: (event: DJSEMBusEvent) => void;
}

const PRIORITY_RANK: Record<DJSEMBusEventPriority, number> = { safety: 0, high: 1, normal: 2, low: 3 };
const SAFETY_EVENTS = new Set(["safe_mode.enabled", "blackout.enabled"]);

const _seq = new SeqCounter();

/**
 * In-process, synchronous, deterministic pub/sub bus for the Studio runtime. Dispatches
 * safety events (`safe_mode`/`blackout`) first and unconditionally; while safe mode or
 * blackout is active, other output-affecting events are suppressed (but still traced) rather
 * than delivered. Keeps a bounded ring buffer of recent traces so a long live set cannot
 * exhaust memory.
 */
export class DJSEMInternalEventBus {
  private subs: DJSEMBusSubscription[] = [];
  private log: DJSEMObservabilityTrace[] = [];
  private obs = new DJSEMObservabilityService();
  private safeMode = false;
  private blackout = false;
  // A live set can dispatch tens of thousands of events; an uncapped in-memory trace
  // log would grow without bound and eventually exhaust memory mid-performance. Keep a
  // bounded ring of the most recent traces. Full history belongs in durable storage.
  private readonly maxLogEntries: number;

  constructor(private sessionId: string = "bus_session", maxLogEntries = 5000) {
    this.maxLogEntries = Math.max(1, maxLogEntries);
  }

  private pushTrace(trace: DJSEMObservabilityTrace): void {
    this.log.push(trace);
    if (this.log.length > this.maxLogEntries) {
      // Drop oldest overflow in one splice rather than shift()-per-push.
      this.log.splice(0, this.log.length - this.maxLogEntries);
    }
  }

  /**
   * Subscribes a handler to a specific event type, or `"*"` for all event types.
   * @param eventType - The event type to listen for, or `"*"` for every event.
   * @param handler - Called synchronously for each matching dispatched event.
   * @returns A subscription handle; pass its `id` to {@link unsubscribe} to remove it.
   */
  subscribe(eventType: string | "*", handler: (e: DJSEMBusEvent) => void): DJSEMBusSubscription {
    const sub: DJSEMBusSubscription = { id: `sub_${_seq.next()}`, eventType, handler };
    this.subs.push(sub);
    return sub;
  }
  /**
   * Removes a subscription by id. No-op if the id is not found.
   * @param id - The subscription id returned by {@link subscribe}.
   */
  unsubscribe(id: string): void { this.subs = this.subs.filter((s) => s.id !== id); }

  // Dispatch a batch deterministically: safety first, then priority rank, then eventId.
  //
  // Effective rank is derived from eventType via SAFETY_EVENTS, NOT read verbatim off
  // ev.priority — a producer that dispatches a real safe_mode/blackout event but forgets (or
  // is tricked into forgetting) to tag it `priority: "safety"` must still jump the queue ahead
  // of every other event in the same batch. Trusting the caller-supplied tag alone would let a
  // same-batch "high" priority output_frame event dispatch to subscribers before blackout.enabled
  // is processed, silently breaking the "safety events dispatch FIRST" guarantee this class's
  // header comment promises.
  private effectiveRank(ev: { eventType: string; priority: DJSEMBusEventPriority }): number {
    return SAFETY_EVENTS.has(ev.eventType) ? PRIORITY_RANK.safety : PRIORITY_RANK[ev.priority];
  }

  /**
   * Dispatches a batch of events deterministically: safety events first, then by priority
   * rank, then by eventId — so ordering never depends on caller/array order.
   * @param events - The events to dispatch (eventId is assigned if not provided).
   * @returns The events actually dispatched, in the order they were sent to subscribers.
   */
  dispatch(events: Array<Omit<DJSEMBusEvent, "eventId"> & { eventId?: string }>): DJSEMBusEvent[] {
    const prepared: DJSEMBusEvent[] = events.map((e) => ({ ...e, eventId: e.eventId ?? `bev_${_seq.next()}` }));
    prepared.sort((a, b) => this.effectiveRank(a) - this.effectiveRank(b) || a.eventId.localeCompare(b.eventId));
    for (const ev of prepared) this.dispatchOne(ev);
    return prepared;
  }

  /**
   * Dispatches a single event: updates safe-mode/blackout state, records a (possibly
   * suppressed) observability trace, and — unless suppressed — invokes matching subscribers.
   * Subscriber handler errors are swallowed so one bad handler never breaks the bus.
   * @param ev - The event to dispatch.
   */
  dispatchOne(ev: DJSEMBusEvent): void {
    // Track safety state — once safe/blackout is on, non-safety output-ish events are
    // suppressed (logged as suppressed) until cleared.
    if (ev.eventType === "safe_mode.enabled") this.safeMode = true;
    if (ev.eventType === "safe_mode.disabled") this.safeMode = false;
    if (ev.eventType === "blackout.enabled") this.blackout = true;
    if (ev.eventType === "blackout.cleared") this.blackout = false;

    const suppressed = (this.safeMode || this.blackout)
      && !SAFETY_EVENTS.has(ev.eventType)
      && !/disabled|cleared/.test(ev.eventType)
      && /output_frame|themed_event|lighting\.scene|adapter\./.test(ev.eventType);

    const trace = this.obs.createTrace({
      sessionId: this.sessionId,
      eventType: (suppressed ? "semantic_event.suppressed" : (ev.eventType as DJSEMTraceEventType)),
      source: "event_bus",
      payload: { busEventId: ev.eventId, original: ev.eventType, priority: ev.priority, payload: ev.payload },
      reason: suppressed ? `suppressed while ${this.blackout ? "blackout" : "safe mode"} active` : undefined,
    });
    this.pushTrace(trace);
    if (suppressed) return;
    for (const s of this.subs) {
      if (s.eventType === "*" || s.eventType === ev.eventType) {
        try { s.handler(ev); } catch { /* subscriber errors never break the bus */ }
      }
    }
  }

  /** @returns A copy of the bounded recent-trace log. */
  getLog(): DJSEMObservabilityTrace[] { return [...this.log]; }
  /** @returns Whether safe mode is currently active. */
  isSafeMode(): boolean { return this.safeMode; }
  /** @returns Whether blackout is currently active. */
  isBlackout(): boolean { return this.blackout; }

  /** @returns A replay log built from this bus's current trace log. */
  exportReplayLog() {
    return this.obs.buildReplayLog({ sessionId: this.sessionId, source: "simulator", events: this.log });
  }
}
